import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subscription } from 'rxjs';
import { InvoiceService } from 'src/app/services/invoice.service';
import { WarehouseInvoiceService, WarehouseReceivable } from 'src/app/services/warehouse-invoice.service';
import { WarehouseService } from 'src/app/services/warehouse.service';

/**
 * Panel "Cuentas por cobrar": totales por antigüedad y grid por cliente.
 *
 * Igual que el panel de facturas, se comparte entre invoicing-list y la
 * pantalla de Facturas de almacén.
 */
@Component({
    selector: 'app-receivable-panel',
    templateUrl: './receivable-panel.component.html',
    styleUrls: ['./receivable-panel.component.css']
})
export class ReceivablePanelComponent implements OnInit, OnDestroy {
    /** Igual que en el panel de facturas: `warehouse` calcula la antigüedad
     *  sobre las facturas de almacén de prueba en vez de pedirla al backend,
     *  que todavía no sabe distinguirlas de las de transporte. */
    @Input() source: 'api' | 'warehouse' = 'api';

    constructor(
        private invoiceService: InvoiceService,
        private warehouseInvoiceService: WarehouseInvoiceService,
        private warehouseService: WarehouseService,
        private spinner: NgxSpinnerService
    ) {}

    /** Almacén activo en modo `warehouse`. */
    private warehouseName: string | null = null;
    private warehouseSub?: Subscription;

    public arTotals = {
        current: 0,
        d1_30: 0,
        d31_60: 0,
        d61_90: 0,
        d91_120: 0,
        d120_plus: 0,
        total: 0
    };
    public arRowData: any[] = [];

    private currencyFormatter = (p: any) => {
        const v = Number(p.value || 0);
        if (!v) return '-';
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v);
    };

    arColumnDefs = [
        { field: 'client_name', headerName: 'Cliente', flex: 1.6, pinned: 'left' as const },
        {
            field: 'current',
            headerName: 'Corriente',
            headerTooltip: 'Días por vencer',
            flex: 1,
            type: 'numericColumn',
            valueFormatter: this.currencyFormatter
        },
        {
            field: 'd1_30',
            headerName: '1 a 30 días',
            headerTooltip: 'Días vencidos',
            flex: 1,
            type: 'numericColumn',
            valueFormatter: this.currencyFormatter
        },
        {
            field: 'd31_60',
            headerName: '31 a 60 días',
            headerTooltip: 'Días vencidos',
            flex: 1,
            type: 'numericColumn',
            valueFormatter: this.currencyFormatter
        },
        {
            field: 'd61_90',
            headerName: '61 a 90 días',
            headerTooltip: 'Días vencidos',
            flex: 1,
            type: 'numericColumn',
            valueFormatter: this.currencyFormatter
        },
        {
            field: 'd91_120',
            headerName: '91 a 120 días',
            headerTooltip: 'Días vencidos',
            flex: 1,
            type: 'numericColumn',
            valueFormatter: this.currencyFormatter
        },
        {
            field: 'd120_plus',
            headerName: '+ 120 días',
            headerTooltip: 'Días vencidos',
            flex: 1,
            type: 'numericColumn',
            valueFormatter: this.currencyFormatter
        },
        {
            field: 'total',
            headerName: 'Total',
            flex: 1,
            type: 'numericColumn',
            cellStyle: { fontWeight: 600 },
            valueFormatter: this.currencyFormatter
        }
    ];

    public defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
    };

    ngOnInit(): void {
        if (this.source === 'warehouse') {
            // BehaviorSubject: la primera emisión es la que dispara la carga
            // inicial, y cada cambio de almacén recalcula la cartera.
            this.warehouseSub = this.warehouseService.current$.subscribe(warehouse => {
                this.warehouseName = warehouse.name;
                this.loadAccountsReceivable();
            });
            return;
        }

        this.loadAccountsReceivable();
    }

    ngOnDestroy(): void {
        this.warehouseSub?.unsubscribe();
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);
    }

    loadAccountsReceivable() {
        this.spinner.show();
        const receivable$: Observable<WarehouseReceivable | any> = this.source === 'warehouse'
            ? this.warehouseInvoiceService.accountsReceivable(undefined, this.warehouseName || undefined)
            : this.invoiceService.accountsReceivable();

        receivable$.subscribe({
            next: (resp) => {
                this.arTotals = {
                    current: Number(resp?.totals?.current ?? 0),
                    d1_30: Number(resp?.totals?.d1_30 ?? 0),
                    d31_60: Number(resp?.totals?.d31_60 ?? 0),
                    d61_90: Number(resp?.totals?.d61_90 ?? 0),
                    d91_120: Number(resp?.totals?.d91_120 ?? 0),
                    d120_plus: Number(resp?.totals?.d120_plus ?? 0),
                    total: Number(resp?.totals?.total ?? 0)
                };
                this.arRowData = resp?.rows || [];
                this.spinner.hide();
            },
            error: () => {
                this.arRowData = [];
                this.spinner.hide();
            }
        });
    }
}
